/**
 * Cable Popup Component - Display cable info with fiber usage in map popup
 */
import React, { useEffect, useState } from 'react';
import { Cable, CableFiberUsage } from '../../types';
import { apiClient } from '../../services/api';
import './CablePopup.css';
import { cableReserve, cableOpticalLength } from '../../utils/cableLength';

interface CablePopupProps {
  cable: Cable;
  projectId: number;
  onViewDetails?: () => void;
}

export const CablePopup: React.FC<CablePopupProps> = ({ cable, projectId, onViewDetails }) => {
  const [usage, setUsage] = useState<CableFiberUsage | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFiberUsage();
  }, [cable.id, projectId]);

  const loadFiberUsage = async () => {
    setLoading(true);
    try {
      const response = await apiClient.cables.getFiberUsage(projectId, cable.id);
      setUsage(response.data);
    } catch (err) {
      console.error('[CablePopup] Error loading fiber usage:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cable-popup">
      <h4>Kábel #{cable.id}</h4>
      
      {usage && (
        <>
          <div className="popup-info">
            <div className="info-line">
              <span className="label">Od:</span>
              <span className="value">{usage.from_pole.name}</span>
            </div>
            <div className="info-line">
              <span className="label">Do:</span>
              <span className="value">{usage.to_pole.name}</span>
            </div>
            <div className="info-line">
              <span className="label">Dĺžka:</span>
              <span className="value">{cable.length_meters}m</span>
            </div>
            {/* Podporné stĺpy, cez ktoré kábel prechádza (neprerušený) */}
            {(cable.support_poles || []).length > 0 && (
              <div className="info-line">
                <span className="label">Podporné stĺpy:</span>
                <span className="value">{(cable.support_poles || []).length} na trase</span>
              </div>
            )}
            {/* Návin ukazujeme len keď naozaj je — nerobí sa na každom stĺpe */}
            {cableReserve(cable) > 0 && (
              <div className="info-line">
                <span className="label">Návin:</span>
                <span className="value">
                  {cableReserve(cable).toFixed(1)}m na {(cable.reserves || []).length}{' '}
                  {(cable.reserves || []).length === 1 ? 'stĺpe' : 'stĺpoch'}
                  {' → optická dĺžka '}{cableOpticalLength(cable).toFixed(1)}m
                </span>
              </div>
            )}
            <div className="info-line">
              <span className="label">Typ:</span>
              <span className="value">{cable.cable_type}</span>
            </div>
          </div>

          <div className="popup-divider"></div>

          <div className="popup-usage">
            <h5>📊 Využitie vlákien:</h5>

            <div className="usage-summary">
              <div className="usage-item fully-used">
                <span className="usage-icon">✅</span>
                <span className="usage-label">Použité:</span>
                <span className="usage-value">
                  {usage.statistics.used_count} ({usage.statistics.used_percent}%)
                </span>
              </div>

              <div className="usage-item free-ends">
                <span className="usage-icon">⚠️</span>
                <span className="usage-label">Polospojené:</span>
                <span className="usage-value">
                  {usage.statistics.half_connected_count} ({usage.statistics.half_connected_percent}%)
                </span>
              </div>

              <div className="usage-item unused">
                <span className="usage-icon">🟢</span>
                <span className="usage-label">Voľné:</span>
                <span className="usage-value">
                  {usage.statistics.free_count} ({usage.statistics.free_percent}%)
                </span>
              </div>
            </div>

            <div className="popup-chart">
              <div className="chart-bar">
                {usage.statistics.used_percent > 0 && (
                  <div
                    className="chart-segment fully-used"
                    style={{ width: `${usage.statistics.used_percent}%` }}
                    title={`Použité: ${usage.statistics.used_percent}%`}
                  />
                )}
                {usage.statistics.half_connected_percent > 0 && (
                  <div
                    className="chart-segment free-ends"
                    style={{ width: `${usage.statistics.half_connected_percent}%` }}
                    title={`Polospojené: ${usage.statistics.half_connected_percent}%`}
                  />
                )}
                {usage.statistics.free_percent > 0 && (
                  <div
                    className="chart-segment unused"
                    style={{ width: `${usage.statistics.free_percent}%` }}
                    title={`Voľné: ${usage.statistics.free_percent}%`}
                  />
                )}
              </div>
            </div>

            {onViewDetails && (
              <button className="view-details-btn" onClick={onViewDetails}>
                📋 Zobraziť detail
              </button>
            )}
          </div>
        </>
      )}

      {loading && (
        <div className="popup-loading">
          ⏳ Načítavam...
        </div>
      )}
    </div>
  );
};

